<script setup lang="ts">
import { ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useIsCompact } from '@/composables/useBreakpoint'
import { PRIMARY_NAV } from '@/nav'
import AppSidebar from './AppSidebar.vue'
import NavIcon from './NavIcon.vue'
import NotificationBell from './NotificationBell.vue'
import UserMenu from './UserMenu.vue'

/**
 * The shell, at three sizes.
 *
 * Phone and tablet keep the bottom bar. Refuelled is used one-handed in a
 * kitchen and in a supermarket aisle (N-04), where a thumb-reachable bar
 * beats a drawer you have to open. Our other apps use a drawer because they
 * are desktop-first tools; this one is not.
 *
 * From `lg` the bar is replaced by a sidebar with room for everything, and
 * the page stops pretending to be 32rem wide.
 */
const route = useRoute()
const isCompact = useIsCompact()
const drawerOpen = ref(false)

// Navigating closes the drawer, and so does widening past the breakpoint:
// hiding it in CSS alone would leave its backdrop over a usable page.
watch(() => route.fullPath, () => (drawerOpen.value = false))
watch(isCompact, (compact) => {
  if (!compact) drawerOpen.value = false
})
</script>

<template>
  <div class="flex h-full flex-col">
    <!-- Where the system bar overlays the page (iOS), start below it. -->
    <header
      class="flex shrink-0 items-center gap-3 border-b border-stone bg-surface px-3 py-2 sm:px-4 lg:px-6 lg:py-3"
      style="padding-top: max(0.5rem, env(safe-area-inset-top))"
    >
      <!-- Everything is in the bottom bar on a phone, so this is only the
           overflow the bar has no room for. -->
      <button
        class="flex h-9 w-9 items-center justify-center rounded-lg border border-stone lg:hidden"
        aria-label="More"
        @click="drawerOpen = true"
      >
        <NavIcon name="add" :size="18" />
      </button>

      <RouterLink to="/" class="flex items-center gap-2">
        <img src="/app-icon.svg" width="28" height="28" alt="" class="shrink-0" />
        <span class="display text-lg leading-none text-moss">Refuelled</span>
        <span class="hidden text-xs text-ink/40 sm:inline">
          <span class="text-zest">Eat,</span>{{ ' ' }}<span class="text-tomato">Train,</span>{{ ' ' }}<span class="text-slate">Play,</span>{{ ' ' }}<span class="text-moss">Repeat.</span>
        </span>
      </RouterLink>

      <div class="ml-auto flex items-center gap-2">
        <NotificationBell />
        <UserMenu />
      </div>
    </header>

    <div class="flex min-h-0 flex-1">
      <aside
        class="hidden min-h-0 w-60 shrink-0 overflow-y-auto overscroll-contain border-r border-stone bg-surface p-3 lg:block"
      >
        <AppSidebar />
      </aside>

      <!-- min-w-0 is load-bearing: a flex child defaults to min-width:auto and
           a wide table would push the whole page sideways. -->
      <main class="min-w-0 flex-1 overflow-y-auto px-4 pt-4 pb-24 sm:px-6 lg:px-8 lg:pb-8">
        <slot />
      </main>
    </div>

    <!-- The bottom bar: phone and tablet only. -->
    <nav
      class="shrink-0 border-t border-stone bg-surface/95 backdrop-blur lg:hidden"
      style="padding-bottom: env(safe-area-inset-bottom)"
    >
      <ul class="mx-auto flex max-w-lg">
        <li v-for="item in PRIMARY_NAV" :key="item.to" class="flex-1">
          <RouterLink
            :to="item.to"
            class="flex flex-col items-center gap-1 py-2.5 text-xs font-medium text-ink/60 transition-colors"
            :exact-active-class="item.exact ? 'text-moss' : ''"
            :active-class="item.exact ? '' : 'text-moss'"
          >
            <NavIcon :name="item.icon" :size="22" />
            {{ item.label }}
          </RouterLink>
        </li>
      </ul>
    </nav>

    <!-- Everything the bottom bar has no room for. -->
    <Teleport to="body">
      <div v-if="drawerOpen" class="fixed inset-0 z-50 lg:hidden">
        <div class="absolute inset-0 bg-scrim/40" @click="drawerOpen = false" />
        <aside
          class="absolute inset-y-0 left-0 flex w-72 max-w-[80vw] flex-col overflow-y-auto bg-paper p-3 shadow-xl"
        >
          <div class="mb-3 flex items-center justify-between px-3">
            <span class="display text-lg text-moss">Refuelled</span>
            <button class="text-sm text-ink/60" @click="drawerOpen = false">Close</button>
          </div>
          <AppSidebar in-drawer />
        </aside>
      </div>
    </Teleport>
  </div>
</template>
